import { DURABLE_PILOT_ID } from './durablePilotContract';
import { patchMessages } from './conversationMessageMap';

const unresolved = item => item._optimistic || item._commandAccepted || !item.conversation_id
  || (item.direction === 'outbound' && !['delivered', 'read', 'failed'].includes(item.status));
// Include template headers/cards and link/referral images, not just the main
// attachment. Canonical resources are JSON trees; cached bytes use these IDs.
export function messageAttachmentIds(item) {
  const ids = [];
  const visit = value => {
    if (!value || typeof value !== 'object') return;
    if (typeof value.id === 'string' && value.id.startsWith('att_')) ids.push(value.id);
    Object.values(value).forEach(visit);
  };
  visit(item.content); visit(item.referral); return ids;
}
// Recently viewed (closed) chats keep their newest messages so a reopen paints at once.
export const RECENT_CONVERSATIONS_KEPT = 50;
export const RECENT_MESSAGES_KEPT = 50;
export function evictMessagingHistory(state, openIds = [], recentIds = []) {
  const open = new Set(openIds), recent = new Set(recentIds.slice(0, RECENT_CONVERSATIONS_KEPT));
  const keep = new Set();
  for (const [conversationId, ids] of Object.entries(state.messageOrderByConversation)) {
    if (open.has(conversationId) || conversationId === DURABLE_PILOT_ID) ids.forEach(id => keep.add(id));
    else if (recent.has(conversationId)) ids.slice(0, RECENT_MESSAGES_KEPT).forEach(id => keep.add(id));
  }
  const removed = Object.keys(state.messages).filter(id => !keep.has(id)
    && state.messages[id].conversation_id !== DURABLE_PILOT_ID && !unresolved(state.messages[id]));
  if (!removed.length) return state;
  const deleted = new Set(removed), affected = new Set(removed.map(id => state.messages[id].conversation_id));
  const messages = patchMessages(state.messages, {}, removed);
  const orders = { ...state.messageOrderByConversation }, scopes = { ...state.apiHydration.messageScopes };
  for (const id of affected) {
    orders[id] = (orders[id] || []).filter(messageId => !deleted.has(messageId));
    // A truncated page is never represented as complete. Opening starts at the
    // existing history endpoint's first page, not at an old pagination cursor.
    delete scopes[id];
  }
  const attachments = { ...state.attachments }, reactions = { ...state.reactions };
  const retainedAttachments = new Set(Object.values(messages).flatMap(messageAttachmentIds));
  const droppedAttachments = new Set(removed.flatMap(id => messageAttachmentIds(state.messages[id])));
  for (const id of droppedAttachments) if (!retainedAttachments.has(id)) delete attachments[id];
  for (const [id, reaction] of Object.entries(reactions)) if (deleted.has(reaction.message_id)) delete reactions[id];
  return { ...state, messages, attachments, reactions, messageOrderByConversation: orders,
    liveFieldPaths: { ...state.liveFieldPaths, messages: patchMessages(state.liveFieldPaths.messages, {}, removed) },
    apiHydration: { ...state.apiHydration, messageScopes: scopes } };
}
